import { gql } from '@apollo/client';
import { getTranslationClient } from '../hooks/useTranslationClient';
import { logger } from '../utils/logger';

// GraphQL queries and mutations
const GET_SUPPORTED_LANGUAGES = gql`
  query GetSupportedLanguages {
    supportedLanguages
  }
`;

const TRANSLATE_TEXT = gql`
  mutation TranslateText($input: TranslateTextInput!) {
    translateText(input: $input) {
      translatedText
      targetLanguage
    }
  }
`;

const TRANSLATE_TEXTS = gql`
  mutation TranslateTexts($input: TranslateTextsInput!) {
    translateTexts(input: $input) {
      translatedTexts
      targetLanguage
    }
  }
`;
const pendingRequests = new Map();

/**
 * Translates an array of text strings to the target language using the GraphQL API
 * @param texts Array of text strings to translate
 * @param targetLang Target language code (e.g., 'DE', 'FR', 'ES')
 * @returns Promise resolving to an array of translated text strings
 */
export async function translateText(texts: string[], targetLang: string): Promise<string[]> {
    // Create a unique key for this request
  const requestKey = `${targetLang}:${texts.join('|')}`;

  // Check if this request is already pending
  if (pendingRequests.has(requestKey)) {
    logger.debug('[Translation Service] Returning existing promise for duplicate request');
    return pendingRequests.get(requestKey);
  }

  try {
    const promise = (async () => {
      try {
        // If target language is English, return the original texts
        if (targetLang === 'EN') {
          logger.debug('[Translation Service] Target language is English, returning original texts');
          return texts;
        }
        
        logger.debug(`[Translation Service] Translating ${texts.length} texts to ${targetLang}`);
        
        // Check if the language is supported
        if (!isLanguageSupportedByDeepL(targetLang)) {
          logger.error(`[Translation Service] Language ${targetLang} is not supported by the translation service`);
          return texts; // Return original texts if language is not supported
        }
        
        // Filter out empty strings and keep track of their positions
        const nonEmptyTexts: string[] = [];
        const emptyIndices: number[] = [];
        
        texts.forEach((text, index) => {
          if (!text || text.trim() === '') {
            emptyIndices.push(index);
            logger.warn(`[Translation Service] Empty string found at index ${index}, skipping translation`);
          } else {
            nonEmptyTexts.push(text);
          }
        });
        
        // If all texts are empty, return the original texts
        if (nonEmptyTexts.length === 0) {
          logger.warn('[Translation Service] All texts are empty, returning original texts');
          return texts;
        }
        
        // Format the target language code for GraphQL enum - replace all hyphens with underscores
        const formattedLang = targetLang.replace(/-/g, '_').toUpperCase();
        logger.debug(`[Translation Service] Formatted language code for GraphQL: ${formattedLang}`);
        
        // Use the Apollo client to make the GraphQL request
        const client = getTranslationClient();
        
        let translatedNonEmptyTexts: string[] = [];
        
        // If there's only one non-empty text, use the single text translation mutation
        if (nonEmptyTexts.length === 1) {
          try {
            logger.debug(`[Translation Service] Making single text translation request for language: ${formattedLang}`);
            logger.debug(`[Translation Service] Text to translate: "${nonEmptyTexts[0].substring(0, 50)}${nonEmptyTexts[0].length > 50 ? '...' : ''}"`);

            const response = await client.mutate({
              mutation: TRANSLATE_TEXT,
              variables: {
                input: {
                  text: nonEmptyTexts[0],
                  targetLanguage: formattedLang
                }
              }
            });

            if (response.data && response.data.translateText) {
              translatedNonEmptyTexts = [response.data.translateText.translatedText];
              logger.debug(`[Translation Service] Received translation: "${nonEmptyTexts[0].substring(0, 30)}..." -> "${translatedNonEmptyTexts[0].substring(0, 30)}..."`);
            } else {
              logger.error('Invalid response format for single text translation:', response);
              translatedNonEmptyTexts = [nonEmptyTexts[0]]; // Fallback to original text
            }
          } catch (error) {
            logger.error('Error in single text translation:', error);

            // Log more details about the error
            if (error instanceof Error) {
              logger.error('Error message:', error);
              logger.error('Error stack:', error);
            }

            // Check if it's a GraphQL error
            if (error && (error as any).graphQLErrors) {
              logger.error('GraphQL errors:', (error as any).graphQLErrors);

              // Check if the language is not supported
              const graphQLErrors = (error as any).graphQLErrors;
              if (graphQLErrors && graphQLErrors.length > 0) {
                const errorMessage = graphQLErrors[0].message;
                if (errorMessage && errorMessage.includes('is not supported')) {
                  logger.error(`Language ${formattedLang} is not supported by the translation service`);
                }
              }
            }

            // Check if it's a network error
            if (error && (error as any).networkError) {
              logger.error('Network error:', (error as any).networkError);
            }

            translatedNonEmptyTexts = [nonEmptyTexts[0]]; // Fallback to original text
          }
        } else {
          // Otherwise, use the batch translation mutation
          try {
            const response = await client.mutate({
              mutation: TRANSLATE_TEXTS,
              variables: {
                input: {
                  texts: nonEmptyTexts,
                  targetLanguage: formattedLang
                }
              }
            });

            if (response.data && response.data.translateTexts) {
              translatedNonEmptyTexts = response.data.translateTexts.translatedTexts;
              logger.debug(`[Translation Service] Received ${translatedNonEmptyTexts.length} translations`);
            } else {
              logger.error('[Translation Service] Invalid response format for batch translation:', response);
              translatedNonEmptyTexts = [...nonEmptyTexts]; // Fallback to original texts
            }
          } catch (error) {
            logger.error('[Translation Service] Error in batch translation:', error);

            // Log more details about the error
            if (error instanceof Error) {
              logger.error('Error message:', error);
              logger.error('Error stack:', error);
            }

            // Check if it's a GraphQL error
            if (error && (error as any).graphQLErrors) {
              logger.error('GraphQL errors:', (error as any).graphQLErrors);

              // Check if the language is not supported
              const graphQLErrors = (error as any).graphQLErrors;
              if (graphQLErrors && graphQLErrors.length > 0) {
                const errorMessage = graphQLErrors[0].message;
                if (errorMessage && errorMessage.includes('is not supported')) {
                  logger.error(`[Translation Service] Language ${formattedLang} is not supported by the translation service`);
                }
              }
            }

            // Check if it's a network error
            if (error && (error as any).networkError) {
              logger.error('Network error:', (error as any).networkError);
            }

            translatedNonEmptyTexts = [...nonEmptyTexts]; // Fallback to original texts
          }
        }
        
        // Check if we got the expected number of translations
        if (translatedNonEmptyTexts.length !== nonEmptyTexts.length) {
          logger.error(`[Translation Service] Expected ${nonEmptyTexts.length} translations, got ${translatedNonEmptyTexts.length}`);
        }
        
        // Merge the translated texts back with the empty strings
        const result = [...texts];
        let nonEmptyIndex = 0;

        for (let i = 0; i < result.length; i++) {
          if (!emptyIndices.includes(i)) {
            // This is a non-empty text that was translated
            if (nonEmptyIndex < translatedNonEmptyTexts.length) {
              result[i] = translatedNonEmptyTexts[nonEmptyIndex];
              nonEmptyIndex++;
            }
          }
        }
        
        return result;
      } catch (error) {
        logger.error('Error translating with GraphQL API:', error);

        // Log more details about the error
        if (error instanceof Error) {
          logger.error('Error message:', error.message);
          logger.error('Error stack:', error.stack);
        }

        // Return the original texts in case of error
        return texts;
      }

    })();
    // Store the promise
    pendingRequests.set(requestKey, promise);

    // Clean up when done
    const result = await promise;
    pendingRequests.delete(requestKey);
    return result;

  } catch (error) {
    pendingRequests.delete(requestKey);
    throw error;
  }
}

/**
 * Gets the supported languages from the GraphQL API
 * @returns Promise resolving to an array of supported language codes
 */
export async function getSupportedLanguages(): Promise<string[]> {
  try {
    const client = getTranslationClient();
    const { data } = await client.query({
      query: GET_SUPPORTED_LANGUAGES
    });
    
    return data.supportedLanguages;
  } catch (error) {
    logger.error('Error getting supported languages:', error);
    
    // Return a default list of supported languages
    return [
      'DE',  // German
      'IT',  // Italian
      'EL',  // Greek
      'UK',  // Ukrainian
      'PL',  // Polish
      'RU',  // Russian
      'SV',  // Swedish
      'NB',  // Norwegian
      'JA',  // Japanese
      'ZH_HANS',  // Chinese Simplified
      'ZH_HANT', // Chinese Traditional
      'KO',  // Korean
      'ID',  // Indonesian
      'AR',  // Arabic
      'PT_BR', // Portuguese (Brazilian)
      'NL', // Dutch
      'EN', // English
      'FR', // French
      'ES', // Spanish
    ];
  }
}

/**
 * Checks if a language is supported by the translation service
 * @param lang Language code to check
 * @returns Boolean indicating if the language is supported
 */
export function isLanguageSupportedByDeepL(lang: string): boolean {
  // This is a fallback list of supported languages
  // In a real application, you would get this from the GraphQL API
  const SUPPORTED_LANGUAGES = [
    'DE',  // German
    'IT',  // Italian
    'EL',  // Greek
    'UK',  // Ukrainian
    'PL',  // Polish
    'RU',  // Russian
    'SV',  // Swedish
    'NB',  // Norwegian
    'JA',  // Japanese
    'ZH_HANS',  // Chinese Simplified
    'ZH_HANT', // Chinese Traditional
    'KO',  // Korean
    'ID',  // Indonesian
    'AR',  // Arabic
    'PT_BR', // Portuguese (Brazilian)
    'PT_PT', // Portuguese (European)
    'NL',    // Dutch
    'EN',    // English
    'EN_GB',  // English (British)
    'EN_US',  // English (American)
    'FR',    // French
    'ES',    // Spanish
    'DA',    // Danish
    'FI',    // Finnish
    'CS',    // Czech
    'ET',    // Estonian
    'HU',    // Hungarian
    'LT',    // Lithuanian
    'LV',    // Latvian
    'RO',    // Romanian
    'SK',    // Slovak
    'SL',    // Slovenian
    'BG',    // Bulgarian
    'TR',    // Turkish
    'FA',    // Persian (Farsi)
    'PA',    // Punjabi
    'TL',    // Tagalog
    'UR',    // Urdu
  ];
  
  // Normalize the language code by replacing hyphens with underscores
  const normalizedLang = lang.toUpperCase().replace(/-/g, '_');
  
  return SUPPORTED_LANGUAGES.includes(normalizedLang);
}
